/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import XRegExp from 'xregexp';

export const NULL = 'null';
export const STRING = 'string';
export const ARRAY = 'array';
export const BOOLEAN = 'boolean';
export const OBJECT = 'object';
export const NUMBER = 'number';
export const INTEGER = 'integer';
export const FUNCTION = 'function';
export const TEXT_REGEX = /^[^\t\n\r]*$/;
export const NAME_REGEX = /^[^\t\n\r]*$/;
export const REGION_REGEX = /^[a-z-]*$/;
export const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
export const NODE_NAME_REGEX = XRegExp('^[\\p{L}\\p{N}_\\-\\. ]+$', 'i');
export const INTENT_NAME_REGEX = XRegExp('^[\\p{L}\\p{N}_\\-\\.]+$', 'i');
export const ENTITY_NAME_REGEX = XRegExp('^[\\p{L}\\p{N}_\\-]+$', 'i');
export const SYSTEM_ENTITY_NAME_REGEX = XRegExp(
  '^sys[\\-]{1}[\\p{L}\\p{N}_\\-]+$',
  'i'
);
export const SYSTEM_PREFIX = XRegExp('^(?!sys[_\\-\\.]{1}).*$');
export const INTENT_NAME_ERROR =
  'can only contain alphabetic characters (A-Z,a-z), numeric characters (0-9), underscores (_), dashes (-) and periods (.).';
export const ENTITY_NAME_ERROR =
  'can only contain alphabetic characters (A-Z,a-z), numeric characters (0-9), underscores (_) and dashes (-).';
export const SYSTEM_PREFIX_ERROR = "cannot start with a prefix of 'sys'.";
export const SYSTEM_ENTITY_NAME_ERROR =
  'can only contain letters, underscores, and hyphens. System entities must start with a prefix of "sys-"';
export const NAME_PATTERN_ERROR =
  'cannot contain the following characters: tab, new line, carriage return.';
export const TEXT_PATTERN_ERROR =
  'cannot contain the following characters: tab, new line, carriage return.';
export const REGION_PATTERN_ERROR = 'can only contain a-z and hyphens.';
export const GUID_PATTERN_ERROR = 'must be a GUID.';
export const SELECTORS = ['condition', 'client', 'user_input', 'body'];
export const DATE_REGEX =
  /^\d{4}-\d{2}-\d{2}T([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9](.\d{3})?Z$/;
export const VERSION_DATE_REGEX =
  /^\d{4}-(0?[1-9]|1[012])-(0?[1-9]|[12][0-9]|3[01])$/;
export const VERSION_PATTERN_ERROR = 'must be in the form YYYY-MM-DD.';
export const DATE_PATTERN_ERROR = 'must be a valid ISO date string.';
export const TIME_REGEX = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
export const TIME_PATTERN_ERROR =
  'must be a valid time between 00:00 and 23:59.';
export const CONTEXT_VARIABLE_NAME_REGEX = XRegExp(
  '^((\\*)|([a-zA-Z0-9_-]+)(\\[a-zA-Z0-9_-]+)*\\*?)$',
  'i'
);
export const CONTEXT_VARIABLE_NAME_ERROR =
  'can only contain alphabetical characters (a-z), number characters (0-9) and underscores (_).';
export const PREBUILT_CONTENT_WORKSPACE_NAME = 'system.entities';
export const UNSUPPORTED_PROPERTIES = 'contains unsupported properties';
export const UNSUPPORTED_OUTPUT_PROPERTIES =
  'of the current node contains the following features not supported in the Simple response editor:';
export const MAX_TIME = 10000;
export const MAX_SOURCE_LENGTH = 512;
export const MAX_TITLE_LENGTH = 512;
export const MAX_LABEL_LENGTH = 2048;
export const MAX_TEXT_LENGTH = 4096;
export const MAX_VALUE_LENGTH = 2048;
export const MAX_DESCRIPTION_LENGTH = 256;
export const MAX_AGENT_AVAILABILITY_MESSAGE_LENGTH = 320;
export const MAX_MESSAGE_TO_HUMAN_AGENT_LENGTH = 256;
export const MAX_TOPIC_LENGTH = 64;
export const OPTIONS_LIMIT = 100;
export const RESPONSES_LIMIT = 20;
export const ACTIONS_LIMIT = 5;
export const VARIATIONS_LIMIT = 100;
export const MAX_QUERY_LENGTH = 512;
export const MAX_FILTER_LENGTH = 512;
export const MAX_MESSAGE_TO_QUERY_SEARCH_SKILL_LENGTH = 512;
export const MAX_MESSAGE_TO_FILTER_SEARCH_SKILL_LENGTH = 512;
export const ALLOWED_QUERY_TYPES_SEARCH_SKILL = [
  'natural_language',
  'discovery_query_language'
];
export const MAX_INTENT_NAME_LENGTH = 128;
export const MAX_INTENT_DESCRIPTION_LENGTH = 128;
export const MAX_INTENT_EXAMPLE_LENGTH = 1024;

export const MAX_ACTION_LENGTH = 128;
export const MAX_ACTION_TITLE_LENGTH = 64;

export const MAX_COLLECTION_TITLE_LENGTH = 64;

export const MAX_CONDITION_OPERATORS = 1; // each condition has at most 1 operator (key)

export const MAX_CONTEXT_VAR_LENGTH = 255;

export const DISAMBIGUATION_PROMPT_MAX_LENGTH = 512;
export const DISAMBIGUATION_NONE_OF_THE_ABOVE_MAX_LENGTH = 512;
export const DISAMBIGUATION_PROMPT_LENGTH_ERRROR =
  'Cannot be longer than 512 characters.';

export const RESPONSE_TYPES = {
  text: 'text',
  option: 'option',
  pause: 'pause',
  image: 'image',
  connect_to_agent: 'connect_to_agent',
  channel_transfer: 'channel_transfer',
  search_skill: 'search_skill'
};

export const ACTIONS_STEPS_HANDLERS_NAME_REGEX = XRegExp(
  '^(?!sys[_\\-\\.]{1})[\\p{L}\\p{N}_\\-\\.]+$',
  'i'
).source;

export const WEB_CHAT_ERROR_BOTNAME_REQUIRED =
  'Public assistant name is required.';
export const WEB_CHAT_ERROR_BOTNAME_LENGTH =
  'Public assistant name has a max length of 40 characters.';
export const WEB_CHAT_HOME_SCREEN_GREETING_REQUIRED = 'Greeting is required.';
export const WEB_CHAT_HOME_SCREEN_GREETING_LENGTH =
  'Greeting has a max length of 90 characters.';

export const WEB_CHAT_HOME_SCREEN_STARTER_REQUIRED =
  'All three conversation starters are required.';
export const WEB_CHAT_HOME_SCREEN_STARTER_LENGTH =
  'Conversation starters have a max length of 35.';

export const WEB_CHAT_LAUNCHER_DESKTOP_TITLE_LENGTH =
  'Desktop launcher notification has a max length of 60.';
export const WEB_CHAT_LAUNCHER_DESKTOP_TITLE_REQUIRED =
  'Greeting message is required for desktop launcher.';
export const WEB_CHAT_LAUNCHER_MOBILE_TITLE_LENGTH =
  'Mobile launcher notification has a max length of 45';
export const WEB_CHAT_LAUNCHER_MOBILE_TITLE_REQUIRED =
  'Greeting message is required for mobile launcher.';

export const WEB_CHAT_SECURITY_PUBLIC_KEY_REQUIRED = 'Public key is required.';

export const WEB_CHAT_SUGGESTIONS_OPTION_LABEL_REQUIRED =
  'Option label is required.';

export const WHATSAPP_INTEGRATION_NAME_REQUIRED =
  'Integration name is required.';
export const WHATSAPP_INTEGRATION_NAME_LENGTH =
  'Integration name has a max length of 128 characters.';

export const WHATSAPP_ACCOUNT_SID_REQUIRED = 'Account SID is required.';
export const WHATSAPP_OPTIONS_CONFIGURED_MESSAGE_REQUIRED =
  'Options configured message is required.';
export const WHATSAPP_RUNTIME_ERROR_MESSAGE_REQUIRED =
  'Runtime error message is required.';
export const WHATSAPP_TRANSFER_ERROR_MESSAGE_REQUIRED =
  'Transfer error message is required.';
export const WHATSAPP_ACCOUNT_SID_REGEX = /^[0-9A-Ca-f]{34}$/;
export const WHATSAPP_ACCOUNT_SID_ERROR =
  'Must be 34 characters long and starting only number characters (0-9) and specific alphabetical characters (A-C and a-f).';

export const WHATSAPP_AUTH_TOKEN_REQUIRED = 'Auth token is required.';
export const WHATSAPP_AUTH_TOKEN_REGEX = /^[0-9a-f]{32}$/;
export const WHATSAPP_AUTH_TOKEN_ERROR =
  'Must be 32 characters long and contain only number characters (0-9) and specific alphabetical characters (a-f).';

export const MAX_CHANNEL_TRANSFER_MESSAGE_TO_USER_LENGTH = 100;
export const CHANNEL_TRANSFER_MESSAGE_TO_USER_LENGTH =
  'The message before link to web chat has a max length of 100 characters.';
export const CHANNEL_TRANSFER_MESSAGE_TO_USER_MAX =
  'has a max length of 100 characters.';
export const CHANNEL_TRANSFER_MESSAGE_TO_USER_MIN = 'requires a value.';
export const CHANNEL_TRANSFER_MESSAGE_REQUIRED =
  'The message before link to web chat is required.';
export const CHANNEL_TRANSFER_INVALID_LINK = 'URL to web chat is not valid.';
export const MAX_SUGGESTIONS_OPTION_LABEL_LENGTH = 100;
export const SUGGESTIONS_OPTION_LABEL_LENGTH =
  'Option label has a max length of 100 characters.';

export const OPERATOR_TYPES = {
  equal: 'eq',
  not_equal: 'neq',
  and: 'and',
  or: 'or',
  less_than_eq: 'lte',
  greater_than_eq: 'gte',
  generic: 'generic'
};

export const OPERATOR_LIST = [
  'eq',
  'neq',
  'and',
  'or',
  'lte',
  'gte',
  'generic'
];

export const OPTION_VALUE_MAX_LENGTH = 64;
export const OPTION_SYNONYM_MAX_LENGTH = 64;
export const GLOBAL_VAR_MAX_LENGTH = 64;
export const PHONE_EDIT_REGEX = /^\+?[0-9\-()\s]+$/;
export const PHONE_EDIT_ERROR =
  'can only contain number characters (0-9) and plus(+), minus(-) and parenthesis.';
export const PHONE_NORMALIZED_REGEX = /^\+\d{4,}/;
export const PHONE_NORMALIZED_ERROR =
  'normalized must be plus sign followed by four or more digits.';

export const SIP_URI_REGEX = /(^sip:|^sips:|^tel:).*$/;
export const SIP_URI_ERROR = '^SIP URI must start with "sip:"|"sips:"|"tel:"';

export const SIP_PATTERN_REGEX = /(^sip:|^sips:)\+?\d+@[a-zA-Z0-9.-]+$/;
export const SIP_PATTERN_ERROR =
  '^Should be a valid SIP URI. Example: sip|sips:+18004441212@company.com';

export const TEL_PATTERN_REGEX = /^tel:\+?\d+$/;
export const TEL_PATTERN_ERROR =
  '^Should be a valid TEL URI. Example: tel:+18004441212';

export const SIP_HEADER_REGEX = /^[!-~]+$/;
export const SIP_HEADER_ERROR =
  '^SIP Header can only contain US-ASCII characters';

export const STEP_VALIDATION_ERROR_RESPONSE_MAX_LENGTH = 128;
export const STEP_VALIDATION_RESPOND_WITH_MAX_LENGTH = 128;
export const STEP_TITLE_MAX_LENGTH = 64;
export const STEP_TITLE_MAX_LENGTH_ERROR = `Text is too long. The maximum length is ${STEP_TITLE_MAX_LENGTH} characters.`;
